import React, { useState, useEffect } from 'react';
import { X, Check, Crown, Sparkles, Zap, Heart, ExternalLink, MessageCircle, Timer } from 'lucide-react';
import { useTheme } from '../context/ThemeContext.tsx';
import { API_URL } from '../config/api.ts';

interface PricingModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentTier?: string;
}

// Configuración de suscripciones
const SUBSCRIPTIONS = [
    {
        id: 'free',
        name: 'Free',
        price: 0,
        dailyMessages: 50,
        context: '8K',
        color: '#6b7280',
        gradient: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
        icon: Heart,
        features: [
            '50 daily messages',
            '8K context window',
            'Access to public characters',
            'Basic AI model'
        ]
    },
    {
        id: 'standard',
        name: 'Standard',
        price: 5,
        dailyMessages: 150,
        context: '16K',
        color: '#3b82f6',
        gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
        icon: Zap,
        features: [
            '150 daily messages',
            '16K context window',
            'Advanced AI models',
            'Access to boosters',
            'Email support'
        ]
    },
    {
        id: 'vip',
        name: 'VIP',
        price: 10,
        dailyMessages: 300,
        context: '24K',
        color: '#8b5cf6',
        gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
        icon: Crown,
        popular: true,
        features: [
            '300 daily messages',
            '24K context window',
            'All AI models',
            'Access to boosters',
            'Early access',
            'Priority support'
        ]
    },
    {
        id: 'simp',
        name: 'SIMP',
        price: 20,
        dailyMessages: 500,
        context: '40K',
        color: '#ec4899',
        gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
        icon: Sparkles,
        features: [
            '500 daily messages',
            '40K context window',
            'Premium AI models',
            'Access to boosters',
            'VIP Discord',
            'Direct support',
            'Exclusive characters'
        ]
    }
];

// Configuración de boosters
const BOOSTERS = [
    { id: 'starter', name: 'Starter', price: 10, messages: 3000, color: '#10b981' },
    { id: 'pro', name: 'Pro', price: 20, messages: 7500, color: '#f59e0b' },
    { id: 'ultra', name: 'Ultra', price: 50, messages: 15000, color: '#ef4444' }
];

const PricingModal: React.FC<PricingModalProps> = ({
    isOpen,
    onClose,
    currentTier = 'free'
}) => {
    const { isDarkMode } = useTheme();
    const [activeTab, setActiveTab] = useState<'subscriptions' | 'boosters'>('subscriptions');
    const [subscribeStarUrl, setSubscribeStarUrl] = useState('');
    const [hasMembership, setHasMembership] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchCheckoutUrls();
            setHasMembership(currentTier !== 'free');
        }
    }, [isOpen, currentTier]);

    const fetchCheckoutUrls = async () => {
        try {
            const response = await fetch(`${API_URL}/subscribestar/checkout-urls`);
            const data = await response.json();
            if (data.success) {
                setSubscribeStarUrl(data.urls.subscribe);
            }
        } catch (error) {
            console.error('Error fetching checkout URLs:', error);
        }
    };

    if (!isOpen) return null;

    const handleSubscribe = (tier: string) => {
        if (subscribeStarUrl) {
            window.open(subscribeStarUrl, '_blank');
        }
    };

    const handleBuyBooster = (boosterId: string) => {
        if (subscribeStarUrl) {
            // Redirigir a la página de tips de SubscribeStar
            window.open(`${subscribeStarUrl}#tip`, '_blank');
        }
    };

    const isCurrentPlan = (planId: string) => {
        return currentTier?.toLowerCase() === planId.toLowerCase();
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.4) 0%, rgba(236, 72, 153, 0.4) 50%, rgba(139, 92, 246, 0.4) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                backdropFilter: 'blur(20px)',
                padding: '16px',
                overflowY: 'auto'
            }}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    width: '100%',
                    maxWidth: '1100px',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '24px'
                }}
            >
                {/* Close Button */}
                <button
                    onClick={onClose}
                    style={{
                        position: 'fixed',
                        top: '20px',
                        right: '20px',
                        background: 'rgba(255,255,255,0.15)',
                        border: 'none',
                        color: 'white',
                        cursor: 'pointer',
                        padding: '12px',
                        borderRadius: '50%',
                        display: 'flex',
                        backdropFilter: 'blur(10px)',
                        zIndex: 1001
                    }}
                >
                    <X size={24} />
                </button>

                {/* Header */}
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <h1 style={{
                        fontSize: 'clamp(28px, 5vw, 48px)',
                        fontWeight: '800',
                        background: 'linear-gradient(135deg, #ec4899 0%, #f472b6 50%, #a855f7 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        margin: '0 0 12px',
                        fontStyle: 'italic'
                    }}>
                        Choose Your Experience
                    </h1>
                    <p style={{
                        fontSize: '14px',
                        color: 'rgba(255,255,255,0.8)',
                        margin: 0
                    }}>
                        Enjoy free access or upgrade for more messages and premium features
                    </p>
                </div>

                {/* Tab Toggle */}
                <div style={{
                    display: 'flex',
                    gap: '4px',
                    padding: '4px',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    borderRadius: '25px',
                    marginBottom: '24px',
                    backdropFilter: 'blur(10px)'
                }}>
                    <button
                        onClick={() => setActiveTab('subscriptions')}
                        style={{
                            padding: '10px 20px',
                            borderRadius: '20px',
                            border: 'none',
                            backgroundColor: activeTab === 'subscriptions' ? 'rgba(168, 85, 247, 0.8)' : 'transparent',
                            color: 'white',
                            fontWeight: '600',
                            fontSize: '14px',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <Timer size={16} />
                        Subscriptions
                    </button>
                    <button
                        onClick={() => setActiveTab('boosters')}
                        style={{
                            padding: '10px 20px',
                            borderRadius: '20px',
                            border: 'none',
                            backgroundColor: activeTab === 'boosters' ? 'rgba(168, 85, 247, 0.8)' : 'transparent',
                            color: 'white',
                            fontWeight: '600',
                            fontSize: '14px',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <Zap size={16} />
                        Boosters
                    </button>
                </div>

                {/* Content */}
                {activeTab === 'subscriptions' ? (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                        gap: '16px',
                        width: '100%'
                    }}>
                        {SUBSCRIPTIONS.map(plan => {
                            const Icon = plan.icon;
                            const isCurrent = isCurrentPlan(plan.name);

                            return (
                                <div
                                    key={plan.id}
                                    style={{
                                        background: 'rgba(255, 255, 255, 0.1)',
                                        backdropFilter: 'blur(20px)',
                                        borderRadius: '20px',
                                        padding: '24px',
                                        border: plan.popular
                                            ? '2px solid rgba(139, 92, 246, 0.6)'
                                            : isCurrent
                                                ? `2px solid ${plan.color}`
                                                : '1px solid rgba(255, 255, 255, 0.15)',
                                        position: 'relative',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        transition: 'transform 0.3s',
                                        boxShadow: plan.popular
                                            ? '0 8px 32px rgba(139, 92, 246, 0.3)'
                                            : '0 4px 20px rgba(0, 0, 0, 0.1)'
                                    }}
                                >
                                    {/* Popular Badge */}
                                    {plan.popular && !isCurrent && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '-10px',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            background: plan.gradient,
                                            color: 'white',
                                            padding: '4px 16px',
                                            borderRadius: '20px',
                                            fontSize: '11px',
                                            fontWeight: '700'
                                        }}>
                                            MOST POPULAR
                                        </div>
                                    )}

                                    {/* Current Badge */}
                                    {isCurrent && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '-10px',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            background: plan.gradient,
                                            color: 'white',
                                            padding: '4px 16px',
                                            borderRadius: '20px',
                                            fontSize: '11px',
                                            fontWeight: '700'
                                        }}>
                                            YOUR PLAN
                                        </div>
                                    )}

                                    {/* Plan Header */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                                        <div style={{
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '12px',
                                            background: `${plan.color}30`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <Icon size={20} color={plan.color} />
                                        </div>
                                        <h3 style={{ color: 'white', fontSize: '20px', fontWeight: '700', margin: 0 }}>
                                            {plan.name}
                                        </h3>
                                    </div>

                                    {/* Price */}
                                    <div style={{ marginBottom: '16px' }}>
                                        <span style={{
                                            fontSize: '32px',
                                            fontWeight: '800',
                                            background: plan.gradient,
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent'
                                        }}>
                                            ${plan.price}
                                        </span>
                                        {plan.price > 0 && (
                                            <span style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)' }}>/mes</span>
                                        )}
                                    </div>

                                    {/* Key Stats */}
                                    <div style={{
                                        display: 'flex',
                                        gap: '8px',
                                        marginBottom: '16px',
                                        padding: '12px',
                                        background: 'rgba(255,255,255,0.05)',
                                        borderRadius: '12px'
                                    }}>
                                        <div style={{ flex: 1, textAlign: 'center' }}>
                                            <div style={{ fontSize: '18px', fontWeight: '700', color: 'white' }}>
                                                {plan.dailyMessages}
                                            </div>
                                            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)' }}>msg/day</div>
                                        </div>
                                        <div style={{ width: '1px', background: 'rgba(255,255,255,0.1)' }} />
                                        <div style={{ flex: 1, textAlign: 'center' }}>
                                            <div style={{ fontSize: '18px', fontWeight: '700', color: 'white' }}>
                                                {plan.context}
                                            </div>
                                            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)' }}>context</div>
                                        </div>
                                    </div>

                                    {/* Features */}
                                    <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', flex: 1 }}>
                                        {plan.features.map((feature, idx) => (
                                            <li key={idx} style={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: '8px',
                                                marginBottom: '8px',
                                                fontSize: '12px',
                                                color: 'rgba(255,255,255,0.8)'
                                            }}>
                                                <Check size={12} color={plan.color} style={{ marginTop: '2px', flexShrink: 0 }} />
                                                <span>{feature}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    {/* CTA Button */}
                                    <button
                                        onClick={() => !isCurrent && plan.price > 0 && handleSubscribe(plan.id)}
                                        disabled={isCurrent || plan.price === 0}
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            borderRadius: '12px',
                                            border: 'none',
                                            background: isCurrent || plan.price === 0
                                                ? 'rgba(255,255,255,0.1)'
                                                : plan.gradient,
                                            color: 'white',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            cursor: isCurrent || plan.price === 0 ? 'default' : 'pointer',
                                            opacity: isCurrent ? 0.7 : 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        {isCurrent ? 'Current Plan' : plan.price === 0 ? 'Free' : (
                                            <>
                                                <ExternalLink size={14} />
                                                Subscribe
                                            </>
                                        )}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div style={{ width: '100%' }}>
                        {/* Booster Info */}
                        {!hasMembership && (
                            <div style={{
                                background: 'rgba(239, 68, 68, 0.2)',
                                border: '1px solid rgba(239, 68, 68, 0.4)',
                                borderRadius: '12px',
                                padding: '16px',
                                marginBottom: '20px',
                                textAlign: 'center'
                            }}>
                                <p style={{ color: '#fca5a5', margin: 0, fontSize: '14px' }}>
                                    ⚠️ Boosters are only available for users with active membership.
                                    <br />
                                    <span style={{ color: 'rgba(255,255,255,0.7)' }}>
                                        Subscribe to any paid plan to unlock this feature.
                                    </span>
                                </p>
                            </div>
                        )}

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                            gap: '16px'
                        }}>
                            {BOOSTERS.map(booster => (
                                <div
                                    key={booster.id}
                                    style={{
                                        background: 'rgba(255, 255, 255, 0.1)',
                                        backdropFilter: 'blur(20px)',
                                        borderRadius: '20px',
                                        padding: '24px',
                                        border: '1px solid rgba(255, 255, 255, 0.15)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        textAlign: 'center',
                                        opacity: hasMembership ? 1 : 0.5
                                    }}
                                >
                                    {/* Icon */}
                                    <div style={{
                                        width: '60px',
                                        height: '60px',
                                        borderRadius: '50%',
                                        background: `${booster.color}30`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginBottom: '16px'
                                    }}>
                                        <Zap size={28} color={booster.color} />
                                    </div>

                                    <h3 style={{ color: 'white', fontSize: '22px', fontWeight: '700', margin: '0 0 8px' }}>
                                        {booster.name}
                                    </h3>

                                    <div style={{
                                        fontSize: '36px',
                                        fontWeight: '800',
                                        color: booster.color,
                                        marginBottom: '4px'
                                    }}>
                                        ${booster.price}
                                    </div>

                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        marginBottom: '16px',
                                        color: 'rgba(255,255,255,0.8)'
                                    }}>
                                        <MessageCircle size={16} />
                                        <span style={{ fontSize: '16px', fontWeight: '600' }}>
                                            {booster.messages.toLocaleString()} messages
                                        </span>
                                    </div>

                                    <p style={{
                                        color: 'rgba(255,255,255,0.6)',
                                        fontSize: '12px',
                                        marginBottom: '20px'
                                    }}>
                                        One-time payment • Never expire • Adds to your quota
                                    </p>

                                    <button
                                        onClick={() => hasMembership && handleBuyBooster(booster.id)}
                                        disabled={!hasMembership}
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            borderRadius: '12px',
                                            border: 'none',
                                            background: hasMembership
                                                ? `linear-gradient(135deg, ${booster.color}, ${booster.color}aa)`
                                                : 'rgba(255,255,255,0.1)',
                                            color: 'white',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            cursor: hasMembership ? 'pointer' : 'not-allowed',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        {hasMembership ? (
                                            <>
                                                <ExternalLink size={14} />
                                                Buy Now
                                            </>
                                        ) : (
                                            'Membership required'
                                        )}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Footer */}
                <p style={{
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.5)',
                    marginTop: '24px',
                    textAlign: 'center'
                }}>
                    Secure payments via SubscribeStar. Cancel anytime.
                </p>
            </div>
        </div>
    );
};

export default PricingModal;
